import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useQueryBuilder } from "../useBuilders";
import {
  DEAL_CHAT,
  DEAL_AGENT_SUGGESTIONS,
  FETCH,
} from "../queryKeys";
import { dealAgentApi } from "src/services/dealInsights/dealAgent";
import { DealAgentChat } from "src/types/dealAgent";
import { defaultDealAgentChat, mergeStreamEventIntoChats } from "src/utils/dealAgent.helper";

export const useDealChatsFetch = (dealId?: string) => { // receive the same key to receive
  const key = [DEAL_CHAT, dealId ?? ''];
  return useQuery<DealAgentChat[]>({
    queryKey: key,
    initialData: [],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/*
 * Mutation that: (Server-Sent Events)
 * - Reads the SSE stream chunk by chunk. ()
 * - Calls queryClient.setQueryData on every chunk → triggers re-render via useDealAgentChats (receive the same key to receive the updated chats)
 * - On error → marks the optimistic chat as failed (creating: false)
 */
export const useCurrentDealChatStream = (dealId?: string) => { // use the mutation to ask the question
  const queryClient = useQueryClient();
  const key = [DEAL_CHAT, dealId ?? ''];

  return useMutation({
    mutationFn: async ({ chatId, question, session }: { chatId: string; question: string; session?: string }) => {
      await dealAgentApi.askQuestion(
        { question, session, dealId },
        (event) => { // set the data to the query client
          queryClient.setQueryData<DealAgentChat[]>(key, (old = []) => {
            const updated = mergeStreamEventIntoChats(old, chatId, event); // merge the stream event into the chats list
            return updated;
          });
        }
      );
    },

    onMutate: ({ chatId, question }: { chatId: string; question: string }) => {
      queryClient.setQueryData<DealAgentChat[]>(key, (old = []) => {
        const chatData: DealAgentChat = {
          ...defaultDealAgentChat,
          id: chatId,
          question
        };
        return [...old, chatData];
      });
    },

    onError: (err: any, { chatId }) => {
      queryClient.setQueryData<DealAgentChat[]>(key, (old = []) =>
        old.map((chat) =>
          chat.id === chatId
            ? {
              ...chat,
              creating: false,
              error: {
                message: err?.message ?? "Something went wrong. Please try again.",
                code: err?.response?.status ?? 0,
              },
            }
            : chat)); // return the updated chat (current)
    },
  });
};
